<template>
  <div class="mobile">
    <div
      class="scroll"
      @pointerdown.once="renderAllPhotos"
      @touchstart.once="renderAllPhotos"
      @scroll.once="renderAllPhotos"
    >
      <div
        v-for="(photo, index) in renderedPhotos"
        :key="index"
        class="photo"
        :class="[getMobilePhotoClass(photo), { 'card-bg-placeholder': shouldShowSkeleton }]"
        @click="$emit('photoClick', index)"
      >
        <SkeletonItem v-if="shouldShowSkeleton" class="skeleton" border-radius="0px" />
        <img
          v-else
          :src="getPreviewImageSrc(photo, 'mobile')"
          :alt="t('photoAlt', { index: index + 1 })"
          class="image"
          :width="getPreviewImageWidth(photo, 'mobile')"
          :height="getPreviewImageHeight(photo, 'mobile')"
          :loading="getPreviewImageLoading(index)"
          :fetchpriority="getPreviewImageFetchPriority(index)"
          decoding="async"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SkeletonItem } from '@experience/core-skeleton-lib';
import { useI18n } from '@experience/i18n';
import { computed, ref } from 'vue';

import ru from '../i18n/ru.json';
import { getMobilePhotoClass } from '../lib/get-mobile-photo-class';
import {
  getPreviewImageFetchPriority,
  getPreviewImageHeight,
  getPreviewImageLoading,
  getPreviewImageSrc,
  getPreviewImageWidth,
} from '../lib/preview-image';

import type { ExperiencePhoto } from '../types/experience';

const INITIAL_MOBILE_PHOTOS_COUNT = 2;

interface Props {
  photos: ExperiencePhoto[];
}

const props = defineProps<Props>();

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const shouldShowSkeleton = computed(() => !props.photos.length);
const shouldRenderAllPhotos = ref(false);
const renderedPhotos = computed(() => {
  if (shouldRenderAllPhotos.value) {
    return props.photos;
  }

  return props.photos.slice(0, INITIAL_MOBILE_PHOTOS_COUNT);
});

defineEmits<{
  photoClick: [index: number];
}>();

const renderAllPhotos = () => {
  shouldRenderAllPhotos.value = true;
};
</script>

<style scoped lang="scss">
.mobile {
  display: none;

  @include mobile {
    position: relative;
    display: block;
    height: 360px;
    margin-right: calc(-1 * var(--alias-space-20));
    margin-left: calc(-1 * var(--alias-space-20));
  }
}

.scroll {
  display: flex;
  gap: var(--alias-space-4);
  height: 360px;
  padding: 0 var(--alias-space-8);
  overflow: auto hidden;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.photo {
  flex-shrink: 0;
  height: 360px;
  overflow: hidden;
  cursor: pointer;
  background: var(--alias-color-skeleton-default);

  &.horizontal {
    width: calc(100vw - 16px - 32px);
    height: 360px;
  }

  &.vertical {
    width: auto;
    height: 360px;
  }
}

.image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo.vertical .image {
  display: block;
  width: auto;
  height: 100%;
  object-fit: contain;
}

.skeleton {
  width: 100%;
  height: 100%;
}

.card-bg-placeholder {
  background: var(--alias-color-skeleton-default);
}
</style>
